/*
 * V6X JOURNEY MAP
 *
 * A persistent 3D terrain rendered behind the examination questions.
 * Each answered question crystallizes a vivid geometric node.
 * A warm-amber brick settles into the wall with each answer.
 *
 * Design:
 *   – Terrain: near-black with teal grid — max contrast backdrop
 *   – Nodes:   vivid spectrum (warm Q1–4 → teal Q5–8 → electric Q9–12) + bloom glow
 *   – Bricks:  warm amber/copper — immediately readable against dark terrain
 *   – Stars:   distant point-cloud for depth
 *
 * Exported: initV6xJourney(canvas) → { plotNode, brickTransition, start, stop }
 */

import * as THREE from 'three'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass }     from 'three/addons/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'
import { OutputPass }     from 'three/addons/postprocessing/OutputPass.js'

/* ── NODE DEFINITIONS — vivid spectrum ──────────────────────────────── */

const NODE_DEFS = [
  // Q1–Q4  warm: gold → orange → amber → red-orange
  { geo: () => new THREE.IcosahedronGeometry(0.22, 0),            color: 0xffc840, emissive: 0xd47800, label: 'role' },
  { geo: () => new THREE.BoxGeometry(0.32, 0.32, 0.32),           color: 0xff7020, emissive: 0xcc3800, label: 'industry' },
  { geo: () => new THREE.SphereGeometry(0.22, 20, 14),            color: 0xffe040, emissive: 0xd49000, label: 'experience' },
  { geo: () => new THREE.OctahedronGeometry(0.26, 0),             color: 0xff4455, emissive: 0xcc1122, label: 'reliance' },
  // Q5–Q8  transitional: teal → emerald → cyan → lime
  { geo: () => new THREE.TetrahedronGeometry(0.28, 0),            color: 0x44ffbb, emissive: 0x008855, label: 'strengths' },
  { geo: () => new THREE.DodecahedronGeometry(0.22, 0),           color: 0x22dd88, emissive: 0x006633, label: 'achievement' },
  { geo: () => new THREE.TorusGeometry(0.18, 0.06, 10, 28),       color: 0x00ffee, emissive: 0x008888, label: 'uncertainty' },
  { geo: () => new THREE.CylinderGeometry(0.09, 0.20, 0.36, 10),  color: 0x88ff44, emissive: 0x338800, label: 'success' },
  // Q9–Q12  electric: sky → violet → magenta → white
  { geo: () => new THREE.ConeGeometry(0.20, 0.40, 8),             color: 0x00ccff, emissive: 0x0055cc, label: 'failure' },
  { geo: () => new THREE.TorusKnotGeometry(0.18, 0.055, 64, 8),   color: 0xaa44ff, emissive: 0x5500cc, label: 'ai-capability' },
  { geo: () => new THREE.SphereGeometry(0.24, 28, 18),            color: 0xff44cc, emissive: 0xaa0088, label: 'human-advantage' },
  { geo: () => new THREE.IcosahedronGeometry(0.28, 1),            color: 0xffffff, emissive: 0x88aacc, label: 'desired' },
]

/* winding path across the terrain */
const POSITIONS = [
  new THREE.Vector3(-3.0,  0, -3.2),
  new THREE.Vector3(-1.2,  0, -2.0),
  new THREE.Vector3( 1.0,  0, -3.0),
  new THREE.Vector3( 2.8,  0, -1.5),
  new THREE.Vector3( 3.4,  0,  0.4),
  new THREE.Vector3( 1.8,  0,  1.6),
  new THREE.Vector3(-0.2,  0,  2.4),
  new THREE.Vector3(-2.2,  0,  1.4),
  new THREE.Vector3(-3.4,  0,  3.2),
  new THREE.Vector3(-1.0,  0,  4.2),
  new THREE.Vector3( 1.4,  0,  4.0),
  new THREE.Vector3( 3.2,  0,  5.0),
]

/* ── TERRAIN HEIGHT — subtle, flat reads better ──────────────────────── */

function terrainHeight(x, z) {
  return (
    Math.sin(x * 0.45) * Math.cos(z * 0.40) * 0.05 +
    Math.sin(x * 1.1 + z * 0.9) * 0.02
  )
}

/* ── INIT ────────────────────────────────────────────────────────────── */

export function initV6xJourney(canvas) {
  if (!canvas) return null

  const isMobile = window.innerWidth < 768
  const W = canvas.clientWidth  || window.innerWidth
  const H = canvas.clientHeight || window.innerHeight

  /* ── RENDERER ───────────────────────────────────────────────────────── */

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: !isMobile,
    alpha: true,
    powerPreference: 'high-performance',
  })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2))
  renderer.setSize(W, H, false)
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = 1.4

  /* ── SCENE ──────────────────────────────────────────────────────────── */

  const scene = new THREE.Scene()
  // deep near-black fog — objects fade gently before clipping
  scene.fog = new THREE.FogExp2(0x020408, 0.055)
  scene.background = new THREE.Color(0x020408)

  /* ── CAMERA ─────────────────────────────────────────────────────────── */

  const camera = new THREE.PerspectiveCamera(52, W / H, 0.1, 90)
  camera.position.set(0, 11, 2)
  camera.lookAt(0, 0, 0)

  const camTarget   = new THREE.Vector3(0, 11, 2)
  const lookTarget  = new THREE.Vector3(0, 0, 0)
  const lookCurrent = new THREE.Vector3(0, 0, 0)

  /* ── LIGHTS ─────────────────────────────────────────────────────────── */

  // Dim hemisphere — warm ground, cool sky — for terrain definition
  scene.add(new THREE.HemisphereLight(0x0a1828, 0x050a0a, 0.9))

  // Key light (cool white)
  const keyLight = new THREE.DirectionalLight(0x99bbdd, 2.0)
  keyLight.position.set(4, 12, 6)
  scene.add(keyLight)

  // Rim — cool teal from behind
  const rimLight = new THREE.DirectionalLight(0x002244, 1.0)
  rimLight.position.set(-6, 5, -4)
  scene.add(rimLight)

  /* ── TERRAIN ─────────────────────────────────────────────────────────── */

  const terrainGeo = new THREE.PlaneGeometry(24, 24, isMobile ? 36 : 72, isMobile ? 36 : 72)
  terrainGeo.rotateX(-Math.PI / 2)
  const tPos = terrainGeo.attributes.position
  for (let i = 0; i < tPos.count; i++) {
    tPos.setY(i, terrainHeight(tPos.getX(i), tPos.getZ(i)))
  }
  terrainGeo.computeVertexNormals()

  // Near-black terrain — maximum contrast with vivid nodes
  const terrainMat = new THREE.MeshStandardMaterial({
    color: 0x020509,
    roughness: 0.98,
    metalness: 0.02,
  })
  scene.add(new THREE.Mesh(terrainGeo, terrainMat))

  // Teal grid — visible but subtle
  const grid = new THREE.GridHelper(24, 40, 0x0d3028, 0x091a14)
  grid.material.transparent = true
  grid.material.opacity = 0.65
  scene.add(grid)

  /* ── STARFIELD ───────────────────────────────────────────────────────── */

  const starCount = isMobile ? 500 : 1000
  const sBuf = new Float32Array(starCount * 3)
  for (let i = 0; i < starCount * 3; i += 3) {
    const theta = Math.random() * Math.PI * 2
    const phi   = Math.acos(2 * Math.random() - 1)
    const r     = 28 + Math.random() * 20
    sBuf[i]   = r * Math.sin(phi) * Math.cos(theta)
    sBuf[i+1] = Math.abs(r * Math.cos(phi)) + 1
    sBuf[i+2] = r * Math.sin(phi) * Math.sin(theta)
  }
  const starGeo = new THREE.BufferGeometry()
  starGeo.setAttribute('position', new THREE.BufferAttribute(sBuf, 3))
  scene.add(new THREE.Points(
    starGeo,
    new THREE.PointsMaterial({ color: 0x99aabb, size: 0.08, sizeAttenuation: true, transparent: true, opacity: 0.55 })
  ))

  /* ── BRICK WALL SETUP ────────────────────────────────────────────────── */

  const WALL_ORIGIN    = new THREE.Vector3(-5.5, 0, 6.5)
  const BRICK_W = 0.58, BRICK_H = 0.24, BRICK_D = 0.28
  const BRICKS_PER_ROW = 4

  // Warm amber/copper — clearly distinct from the blue-dark terrain
  const brickMat = new THREE.MeshStandardMaterial({
    color: 0xe8922a,
    emissive: 0x7a3808,
    emissiveIntensity: 0.45,
    roughness: 0.72,
    metalness: 0.18,
  })
  const brickGeo  = new THREE.BoxGeometry(BRICK_W, BRICK_H, BRICK_D)
  const mortarMat = new THREE.MeshStandardMaterial({ color: 0x1e1006, roughness: 0.95 })

  /* ── NODE STATE ──────────────────────────────────────────────────────── */

  const nodes    = []
  const particles = []

  /* ── PARTICLE BURST ──────────────────────────────────────────────────── */

  function spawnParticles(pos, color) {
    const count = isMobile ? 10 : 20
    const pGeo  = new THREE.SphereGeometry(0.045, 4, 4)
    const pMatBase = new THREE.MeshBasicMaterial({ color, transparent: true })
    for (let i = 0; i < count; i++) {
      const p = new THREE.Mesh(pGeo, pMatBase.clone())
      p.position.copy(pos)
      const theta = Math.random() * Math.PI * 2
      const phi   = Math.random() * Math.PI
      const spd   = 1.0 + Math.random() * 1.8
      p._vel = new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * spd,
        Math.abs(Math.cos(phi)) * spd * 1.3,
        Math.sin(phi) * Math.sin(theta) * spd
      )
      p._life = 0.7 + Math.random() * 0.5
      p._age  = 0
      scene.add(p)
      particles.push(p)
    }
  }

  /* ── PLOT NODE ───────────────────────────────────────────────────────── */

  function plotNode(questionIndex) {
    if (questionIndex >= POSITIONS.length) return

    const def      = NODE_DEFS[questionIndex]
    const worldPos = POSITIONS[questionIndex].clone()
    worldPos.y = terrainHeight(worldPos.x, worldPos.z) + 0.38

    /* node mesh */
    const mesh = new THREE.Mesh(
      def.geo(),
      new THREE.MeshStandardMaterial({
        color: def.color,
        emissive: def.emissive,
        emissiveIntensity: 0.65,
        roughness: 0.22,
        metalness: 0.55,
      })
    )
    mesh.position.copy(worldPos)
    mesh.scale.setScalar(0)
    scene.add(mesh)

    /* glow point light */
    const glow = new THREE.PointLight(def.color, 3.5, 3.5)
    glow.position.copy(worldPos).setY(worldPos.y + 0.5)
    scene.add(glow)

    nodes.push({ mesh, glow, phase: 0, rotSpeed: 0.28 + Math.random() * 0.55 })

    /* arc line to previous node */
    if (questionIndex > 0) {
      const prev  = POSITIONS[questionIndex - 1].clone()
      prev.y = terrainHeight(prev.x, prev.z) + 0.38
      const pts = []
      for (let s = 0; s <= 24; s++) {
        const t = s / 24
        const p = prev.clone().lerp(worldPos, t)
        p.y += Math.sin(t * Math.PI) * 0.4
        pts.push(p)
      }
      const lineGeo = new THREE.BufferGeometry().setFromPoints(pts)
      scene.add(new THREE.Line(lineGeo, new THREE.LineBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.25,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })))
    }

    /* camera — track centroid of placed nodes, pull back over time */
    const n = questionIndex
    const centroid = new THREE.Vector3()
    for (let i = 0; i <= n; i++) centroid.add(POSITIONS[i])
    centroid.divideScalar(n + 1)

    const spread  = Math.min(n * 0.45, 5.5)
    const height  = 6 + spread
    const depth   = 3.5 + spread
    camTarget.set(centroid.x * 0.5, height, centroid.z * 0.3 + depth)
    lookTarget.copy(centroid).setY(0)

    spawnParticles(worldPos, def.color)
  }

  /* ── BRICK TRANSITION ────────────────────────────────────────────────── */

  const activeBricks = []

  function brickTransition(brickIndex, onDone) {
    const row    = Math.floor(brickIndex / BRICKS_PER_ROW)
    const col    = brickIndex % BRICKS_PER_ROW
    const offset = row % 2 === 0 ? 0 : BRICK_W * 0.5
    const bx = WALL_ORIGIN.x + col * (BRICK_W + 0.020) + offset
    const bz = WALL_ORIGIN.z
    const by = WALL_ORIGIN.y + row * (BRICK_H + 0.015) + BRICK_H * 0.5
    const groundY   = by
    const dropHeight = groundY + 4.0

    const brick = new THREE.Mesh(brickGeo, brickMat.clone())
    brick.position.set(bx, dropHeight, bz)
    scene.add(brick)

    const spinX = (Math.random() - 0.5) * 0.28
    const spinZ = (Math.random() - 0.5) * 0.18

    let elapsed  = 0
    const dur    = 0.50
    let settled  = false
    let bounceT  = 0
    let doneFired = false

    brick._tick = (dt) => {
      elapsed += dt
      if (!settled) {
        const t = Math.min(elapsed / dur, 1)
        brick.position.y  = dropHeight + (groundY - dropHeight) * (t * t)
        brick.rotation.x  = spinX * (1 - t)
        brick.rotation.z  = spinZ * (1 - t)
        if (t >= 1) {
          settled = true
          brick.position.y = groundY
          brick.rotation.set(0, 0, 0)
          bounceT = 0
          // mortar joint
          const mGeo   = new THREE.BoxGeometry(BRICK_W + 0.01, 0.013, BRICK_D + 0.01)
          const mortar = new THREE.Mesh(mGeo, mortarMat)
          mortar.position.set(bx, groundY - BRICK_H * 0.5 + 0.007, bz)
          scene.add(mortar)
          // small glow flash on the brick
          const flash = new THREE.PointLight(0xffaa44, 4.0, 1.5)
          flash.position.set(bx, groundY + BRICK_H * 0.5, bz)
          scene.add(flash)
          setTimeout(() => scene.remove(flash), 280)
          if (!doneFired) { doneFired = true; setTimeout(onDone, 220) }
        }
      } else {
        bounceT += dt
        brick.position.y = groundY + Math.abs(Math.sin(bounceT * 18)) * Math.exp(-bounceT * 12) * 0.045
      }
    }

    activeBricks.push(brick)
  }

  /* ── POST-PROCESSING (bloom) ─────────────────────────────────────────── */

  const composer = new EffectComposer(renderer)
  composer.addPass(new RenderPass(scene, camera))

  const bloom = new UnrealBloomPass(
    new THREE.Vector2(W, H),
    isMobile ? 0.9 : 1.4,  // strength
    0.5,                     // radius
    0.15                     // threshold — lower = more objects glow
  )
  composer.addPass(bloom)
  composer.addPass(new OutputPass())

  /* ── RENDER LOOP ─────────────────────────────────────────────────────── */

  let rafId = null
  let lastT  = 0

  function tick(t) {
    rafId = requestAnimationFrame(tick)
    const dt = Math.min((t - lastT) * 0.001, 0.05)
    lastT = t

    /* camera lerp */
    camera.position.lerp(camTarget, dt * 1.6)
    lookCurrent.lerp(lookTarget, dt * 1.6)
    camera.lookAt(lookCurrent)

    /* node entrance + idle rotation */
    for (const n of nodes) {
      if (n.phase < 1) {
        n.phase = Math.min(1, n.phase + dt * 2.0)
        const e = n.phase < 0.5 ? 4 * n.phase ** 3 : 1 - (-2 * n.phase + 2) ** 3 / 2
        n.mesh.scale.setScalar(e * (1 + Math.sin(n.phase * Math.PI) * 0.22))
        // glow pulses in as node appears
        n.glow.intensity = e * 3.5
      }
      n.mesh.rotation.y += n.rotSpeed * dt
    }

    /* particles */
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i]
      p._age += dt
      const life = p._age / p._life
      if (life >= 1) { scene.remove(p); particles.splice(i, 1); continue }
      p.position.addScaledVector(p._vel, dt)
      p._vel.y -= 2.8 * dt
      p.material.opacity = 1 - life
      p.scale.setScalar(1 - life * 0.7)
    }

    /* falling bricks */
    for (let i = activeBricks.length - 1; i >= 0; i--) {
      const b = activeBricks[i]
      if (b._tick) b._tick(dt)
      else activeBricks.splice(i, 1)
    }

    composer.render()
  }

  function start() {
    if (rafId) return
    lastT = performance.now()
    rafId = requestAnimationFrame(tick)
  }

  function stop() {
    if (rafId) { cancelAnimationFrame(rafId); rafId = null }
  }

  function resize() {
    const w = canvas.clientWidth  || window.innerWidth
    const h = canvas.clientHeight || window.innerHeight
    renderer.setSize(w, h, false)
    camera.aspect = w / h
    camera.updateProjectionMatrix()
    composer.setSize(w, h)
    bloom.resolution.set(w, h)
  }

  window.addEventListener('resize', resize)

  return { plotNode, brickTransition, start, stop, resize }
}
